import { html } from 'lit';
import { DataTableConfig, ColumnDefinition, FilterDefinition, ActionDefinition } from '../../common/data-table/index.js';
import { Part } from '../../../types/part.types.js';

export function createPartsTableConfig(
  parts: Part[],
  assets: Array<{ id: string; model?: { name: string } }>,
  handlers: {
    onRowClick: (part: Part) => void;
    onAddPart: () => void;
    onFilterChange?: (filters: Record<string, any>) => void;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
  }
): DataTableConfig<Part> {

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '—';
    try {
      return new Date(timestamp).toLocaleDateString();
    } catch { 
      return '—'; 
    }
  };

  const getStockPercentage = (part: Part) => {
    if (!part.max_qty || part.max_qty === 0) return 0;
    return Math.round((part.stock_qty / part.max_qty) * 100);
  };

  const getStockStatusBadge = (part: Part) => {
    if (part.stock_qty <= 0) {
      return html`<sl-badge variant="danger">Out of Stock</sl-badge>`;
    } else if (part.stock_qty <= part.reorder_level) {
      return html`<sl-badge variant="warning">Low Stock</sl-badge>`;
    } else if (part.stock_qty >= part.max_qty * 0.9) {
      return html`<sl-badge variant="success">Well Stocked</sl-badge>`;
    } else {
      return html`<sl-badge variant="neutral">In Stock</sl-badge>`;
    }
  };

  const getStockProgressVariant = (part: Part) => {
    if (part.stock_qty <= 0) return 'danger';
    if (part.stock_qty <= part.reorder_level) return 'warning';
    if (part.stock_qty >= part.max_qty * 0.9) return 'success';
    return 'primary';
  };

  const getAssetName = (part: Part) => {
    if (!part.asset_id) return '—';
    const asset = assets.find(a => a.id === part.asset_id);
    return asset?.model?.name || asset?.id || 'Unknown Asset';
  };

  const columns: ColumnDefinition<Part>[] = [
    {
      key: 'name',
      label: 'Part Name',
      type: 'custom',
      sortable: true,
      renderer: (value: string, part: Part) => html`
        <span class="primary">${value || 'Unnamed'}</span>
      `
    },
    {
      key: 'description',
      label: 'Description',
      type: 'text',
      sortable: true,
      truncate: 50
    },
    {
      key: 'asset_name',
      label: 'Associated Asset',
      type: 'custom',
      sortable: true,
      renderer: (value: any, part: Part) => html`
        <span>${getAssetName(part)}</span>
      `
    },
    {
      key: 'stock_qty',
      label: 'Stock Level',
      type: 'custom',
      sortable: true,
      className: 'stock-cell',
      renderer: (value: number, part: Part) => html`
        <div class="stock-info">
          <div class="stock-qty">${part.stock_qty} / ${part.max_qty}</div>
          <sl-progress-bar 
            value=${getStockPercentage(part)}
            variant=${getStockProgressVariant(part)}
            style="margin-top: 4px; width: 100px;">
          </sl-progress-bar>
        </div>
      `
    },
    {
      key: 'cost',
      label: 'Cost',
      type: 'custom',
      sortable: true,
      className: 'cost-cell',
      renderer: (value: number, part: Part) => html`
        <span>${formatCurrency(part.cost)}</span>
      `
    },
    {
      key: 'status',
      label: 'Status',
      type: 'custom',
      sortable: true,
      className: 'status-cell',
      renderer: (value: any, part: Part) => getStockStatusBadge(part)
    },
    {
      key: 'updated_at',
      label: 'Last Updated',
      type: 'custom',
      sortable: true,
      className: 'updated-col',
      renderer: (value: any, part: Part) => html`
        <span>${formatDate(part.updated_at)}</span>
      `
    }
  ];

  const filters: FilterDefinition[] = [
    {
      key: 'stock_status',
      type: 'select',
      label: 'Stock Status',
      placeholder: 'All status',
      options: [
        { value: 'out_of_stock', label: 'Out of Stock' },
        { value: 'low_stock', label: 'Low Stock' },
        { value: 'in_stock', label: 'In Stock' },
        { value: 'well_stocked', label: 'Well Stocked' }
      ]
    },
    {
      key: 'cost_range',
      type: 'select',
      label: 'Cost Range',
      placeholder: 'All costs',
      options: [
        { value: '0-10', label: '$0 - $10' },
        { value: '10-50', label: '$10 - $50' },
        { value: '50-100', label: '$50 - $100' },
        { value: '100+', label: '$100+' }
      ]
    }
  ];

  const rowActions: ActionDefinition<Part>[] = [];

  const headerActions: ActionDefinition[] = [
    {
      key: 'add',
      label: 'Add Part',
      variant: 'primary',
      icon: 'plus',
      size: 'medium',
      onClick: (item: any, event: Event) => {
        handlers.onAddPart();
      }
    }
  ];

  return {
    columns,
    data: parts,
    filters,
    
    headerActions,
    searchPlaceholder: 'Search parts by name, description, or part number…',
    pagination: {
      enabled: true,
      dynamic: true
    },
    emptyState: {
      icon: 'box',
      message: 'No parts found. Try adjusting filters or click "Add Part" to create your first part.'
    },
    clickableRows: true,
    defaultSort: {
      key: 'name',
      direction: 'asc'
    },
    onRowClick: handlers.onRowClick,
    onFilterChange: handlers.onFilterChange,
    onSort: handlers.onSort
  };
}
